import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CredentialDocumentIntent } from "@api/shared-runtime/enums";
import {
  UploadDocuments,
  type UploadDocumentsProps,
} from "@/components/UploadDocuments";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import {
  vaultSubjectUserId,
  vaultCandidatesQueryKey,
  vaultFilesQueryOptions,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import { useTRPC } from "@/trpc";

const PROVIDER_UPLOAD_HEIGHT = 200;
const MINIMUM_UPLOAD_DOCUMENTS = 1;

export function ProviderCredentialUploadPanel({
  scope,
  uploaderId,
  isOpen,
  onUploadStateChange,
}: {
  scope: CredentialVaultScope;
  uploaderId: string;
  isOpen: boolean;
  onUploadStateChange: UploadDocumentsProps["onUploadStateChange"];
}) {
  const userId = vaultSubjectUserId(scope);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  async function refreshReviewQueue() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: vaultCandidatesQueryKey(trpc, scope),
      }),
      queryClient.invalidateQueries({
        queryKey: vaultFilesQueryOptions(trpc, scope).queryKey,
      }),
    ]);
  }

  return (
    <div
      id={uploaderId}
      hidden={!isOpen}
      className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
    >
      <p className={PROFILE_FORM_STYLES.description}>
        Add any credential document. We’ll read it, then ask you to review its
        details and choose where it belongs.
      </p>
      <UploadDocuments
        autoUpload
        minDocuments={MINIMUM_UPLOAD_DOCUMENTS}
        height={PROVIDER_UPLOAD_HEIGHT}
        user={{ id: userId }}
        switchUserId={userId}
        expectedAffiliationId={
          scope.kind === "organization"
            ? scope.expectedAffiliationId
            : undefined
        }
        organizationBusinessUnitId={
          scope.kind === "organization"
            ? scope.organizationBusinessUnitId
            : undefined
        }
        intent={CredentialDocumentIntent.Review}
        onFilesProcessed={refreshReviewQueue}
        onFilesUploaded={() => {
          toast.success("Documents uploaded — review them below");
        }}
        onUploadStateChange={onUploadStateChange}
      />
      <p className="text-xs leading-relaxed text-slate-500">
        Nothing is added to your credentials until you confirm it. You can
        upload several files across any credential category.
      </p>
    </div>
  );
}
